import {Injectable} from '@angular/core';
import {HttpContextToken} from '@angular/common/http';
import {Observable, of, throwError} from 'rxjs';
import {catchError, finalize, map, shareReplay, tap} from 'rxjs/operators';
import {BaseService} from '../../utils/base.service';
import {ServiceTokenService} from './service-token.service';

/**
 * Marks a request that has ALREADY been replayed after a silent refresh.
 *
 * Carried in Angular's HttpContext, which never reaches the network — deliberately not a header.
 * A marker header would be a new custom header on every replayed request, and the API's CORS
 * allow-list is loaded from the `api_cors_origin` table; anything not on it fails the pre-flight,
 * so a retry marker could turn "the token expired" into "the request never left the browser".
 */
export const AUTH_REFRESH_RETRIED = new HttpContextToken<boolean>(() => false);

/**
 * Silent session renewal for the console.
 *
 * With a ~15-minute access token, a 401 on an ordinary call is the routine end of one access token
 * inside a session that is still valid — not the end of the session. This turns that into a renewal
 * and a replay, so the user never sees it.
 *
 * SINGLE-FLIGHT MATTERS MORE HERE THAN ANYWHERE ELSE IN THE PLATFORM. The console dashboard fires
 * roughly fifteen requests in one burst on load. When the access token has expired they all come
 * back 401 together. Refresh tokens rotate — redeeming one consumes it, and presenting a consumed
 * token is precisely how a stolen token is detected, which revokes the whole session. Fifteen
 * independent refreshes would mean one success and fourteen apparent replay attacks: the user
 * would be signed out for opening the dashboard. Every caller therefore shares ONE in-flight
 * request, and the server keeps a short grace window for the races no client-side lock can see
 * (a second tab, or a request already on the wire).
 */
@Injectable({providedIn: 'root'})
export class AuthRefreshService {

  private inFlight$: Observable<boolean> | null = null;

  constructor(private baseService: BaseService, private serviceTokenService: ServiceTokenService) {}

  /**
   * Renew the session, shared across every concurrent caller.
   *
   * Resolves `true` when the cookies were replaced and the caller should replay its request, and
   * `false` when the session is genuinely over. Never throws — a refused refresh is an ordinary
   * outcome that the caller turns into its normal teardown.
   */
  refresh(): Observable<boolean> {
    if (!this.inFlight$) {
      // skipLoader: renewal is invisible to the user, so it must not raise the global spinner.
      // skipError: a failed renewal is reported by the caller as the session ending, once.
      this.inFlight$ = this.baseService.post('auth/refresh-token', {}, true, true).pipe(
        map(() => true),
        // The cached bearer token was minted from the OLD access token and is now stale. Drop it so
        // the next cross-host call (AI API, Node API) fetches one that matches the new session,
        // instead of failing on a token the server has just retired.
        tap(() => this.serviceTokenService.clear()),
        catchError(() => of(false)),
        finalize(() => { this.inFlight$ = null; }),
        shareReplay(1)
      );
    }

    return this.inFlight$;
  }
}
